// Appearance themes for the Apple TV clone surfaces.
//
// "system"          — follow the OS light/dark preference (prefers-color-scheme).
// "dark-glass"      — the original look: near-black pages with frosted
//                     liquid-glass chrome.
// "aluminum-light"  — Siri-remote-inspired: white pages, a silver sidebar
//                     with a subtle brushed-aluminum texture, and solid
//                     black circular buttons with white icons.
//
// The preferred theme is persisted in localStorage. The resolved light/dark
// look is applied as a class on <body> so every Apple page (all of which
// render AppleTvSideNav) picks it up without prop drilling.

const STORAGE_KEY = "curator:appleTheme";
const BODY_CLASS = "apple-theme-aluminum-light";
const SYSTEM_MQ = "(prefers-color-scheme: dark)";
/** One-time flip so System is the selected default after it was introduced. */
const SYSTEM_DEFAULT_MIGRATION_KEY = "curator:appleThemeSystemDefaultV1";

export const APPLE_THEME_SYSTEM = "system";
export const APPLE_THEME_DARK_GLASS = "dark-glass";
export const APPLE_THEME_ALUMINUM_LIGHT = "aluminum-light";

export const APPLE_THEMES = [
  {
    id: APPLE_THEME_SYSTEM,
    name: "System",
    description: "Match your device light or dark mode.",
  },
  {
    id: APPLE_THEME_DARK_GLASS,
    name: "Dark Glass",
    description: "Dark pages with frosted glass chrome.",
  },
  {
    id: APPLE_THEME_ALUMINUM_LIGHT,
    name: "Aluminum Light",
    description: "Light pages with aluminum sidebar.",
  },
];

function isKnownTheme(value) {
  return (
    value === APPLE_THEME_SYSTEM
    || value === APPLE_THEME_ALUMINUM_LIGHT
    || value === APPLE_THEME_DARK_GLASS
  );
}

function persistThemePreference(theme) {
  try {
    window.localStorage.setItem(STORAGE_KEY, theme);
  } catch (_err) {
    // Storage unavailable (private mode); theme still applies for the session.
  }
}

/** Ensure System is the stored default (once), then return the preference. */
export function ensureAppleThemeDefault() {
  try {
    if (!window.localStorage.getItem(SYSTEM_DEFAULT_MIGRATION_KEY)) {
      persistThemePreference(APPLE_THEME_SYSTEM);
      window.localStorage.setItem(SYSTEM_DEFAULT_MIGRATION_KEY, "1");
    } else if (!isKnownTheme(window.localStorage.getItem(STORAGE_KEY))) {
      persistThemePreference(APPLE_THEME_SYSTEM);
    }
  } catch (_err) {
    // no-op
  }
  return getStoredAppleTheme();
}

export function getSystemPrefersLight() {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return true;
  }
  return !window.matchMedia(SYSTEM_MQ).matches;
}

/** Map a stored preference to the concrete theme that should paint. */
export function resolveAppleTheme(preference) {
  if (preference === APPLE_THEME_ALUMINUM_LIGHT) return APPLE_THEME_ALUMINUM_LIGHT;
  if (preference === APPLE_THEME_DARK_GLASS) return APPLE_THEME_DARK_GLASS;
  return getSystemPrefersLight() ? APPLE_THEME_ALUMINUM_LIGHT : APPLE_THEME_DARK_GLASS;
}

/** Stored preference (system / light / dark). Defaults to system. */
export function getStoredAppleTheme() {
  try {
    const value = window.localStorage.getItem(STORAGE_KEY);
    if (isKnownTheme(value)) return value;
    return APPLE_THEME_SYSTEM;
  } catch (_err) {
    return APPLE_THEME_SYSTEM;
  }
}

export function applyAppleTheme(preference = getStoredAppleTheme()) {
  if (typeof document === "undefined") return;
  const resolved = resolveAppleTheme(preference);
  const isLight = resolved === APPLE_THEME_ALUMINUM_LIGHT;
  document.body.classList.toggle(BODY_CLASS, isLight);
  // Drive native form controls / scrollbars with the resolved look.
  document.documentElement.style.colorScheme = isLight ? "light" : "dark";
  document.documentElement.dataset.appleTheme = resolved;
  document.body.dataset.appleTheme = resolved;
  document.body.dataset.appleThemePreference = isKnownTheme(preference)
    ? preference
    : APPLE_THEME_SYSTEM;
}

export function clearAppleThemeClass() {
  if (typeof document === "undefined") return;
  document.body.classList.remove(BODY_CLASS);
  document.documentElement.style.colorScheme = "";
  delete document.documentElement.dataset.appleTheme;
  delete document.body.dataset.appleTheme;
  delete document.body.dataset.appleThemePreference;
}

export function setAppleTheme(theme) {
  const next = isKnownTheme(theme) ? theme : APPLE_THEME_SYSTEM;
  persistThemePreference(next);
  applyAppleTheme(next);
  return next;
}

/** Keep System mode in sync when the OS appearance changes. */
export function subscribeAppleSystemTheme(onChange) {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => {};
  }
  const mq = window.matchMedia(SYSTEM_MQ);
  const handler = () => {
    if (getStoredAppleTheme() !== APPLE_THEME_SYSTEM) return;
    applyAppleTheme(APPLE_THEME_SYSTEM);
    if (typeof onChange === "function") onChange(APPLE_THEME_SYSTEM);
  };
  if (typeof mq.addEventListener === "function") {
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }
  mq.addListener(handler);
  return () => mq.removeListener(handler);
}
